import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { submitFeedback, withTimeout, type FeedbackKind } from '../lib/functions';

/**
 * "Suggest a feature" — the one control that is on every signed-in page.
 *
 * A teacher who hits a rough edge is on the page where it happened, and that
 * is the moment worth catching: by the time they think to email anyone the
 * detail is gone. So this floats in the corner of the layout rather than
 * living on a Help page nobody visits. The page they were on goes with the
 * message; who they are comes from the verified caller server-side, never from
 * here. Where it goes: the `feedback` collection plus an email to ParkerTech.
 */

const KINDS: { value: FeedbackKind; label: string; hint: string }[] = [
  { value: 'feature', label: 'Suggest a feature', hint: 'Something SchoolOne should do, or do differently.' },
  { value: 'problem', label: 'Something is not working', hint: 'Tell us what you expected and what happened instead.' },
  { value: 'other',   label: 'Other feedback', hint: 'Anything else.' },
];

const MESSAGE_MAX = 2000;

export default function FeedbackButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<FeedbackKind>('feature');
  const [message, setMessage] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Escape closes; focus lands in the textarea on open.
  useEffect(() => {
    if (!open) return;
    textareaRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const close = () => {
    setOpen(false);
    // A sent form starts fresh next time; a draft that was not sent is kept.
    if (state === 'sent') { setMessage(''); setKind('feature'); setState('idle'); }
    setError(null);
  };

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = message.trim();
    if (!text || state === 'sending') return;
    setState('sending'); setError(null);
    try {
      await withTimeout(submitFeedback({
        kind,
        message: text,
        path: location.pathname + location.search,
        userAgent: navigator.userAgent,
      }));
      setState('sent');
    } catch (err) {
      setState('error');
      setError(err instanceof Error && err.message && !/internal/i.test(err.message)
        ? err.message
        : 'We could not send that just now. Please try again in a moment.');
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="print:hidden fixed bottom-4 right-4 z-30 inline-flex items-center gap-2 rounded-full bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-lg hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
          <path d="M10 2.5a5 5 0 0 0-2.5 9.33V13a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1.17A5 5 0 0 0 10 2.5ZM8.5 16h3M9 17.5h2"
            stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="hidden sm:inline">Suggest a feature</span>
        <span className="sm:hidden">Feedback</span>
      </button>

      {open && (
        <div className="print:hidden fixed inset-0 z-50 flex items-end justify-center bg-black/30 p-4 sm:items-center" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
          <div role="dialog" aria-modal="true" aria-labelledby="feedback-title" className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
            {state === 'sent' ? (
              <div className="p-6">
                <h2 id="feedback-title" className="text-lg font-semibold text-gray-900">Thank you</h2>
                <p className="mt-2 text-sm text-gray-600">
                  We have got it. Suggestions from the people using SchoolOne every day are how it gets better,
                  and we read every one.
                </p>
                <div className="mt-5 flex justify-end">
                  <button type="button" onClick={close} className="btn-primary">Close</button>
                </div>
              </div>
            ) : (
              <form onSubmit={send} className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 id="feedback-title" className="text-lg font-semibold text-gray-900">Suggest a feature or send feedback</h2>
                    <p className="mt-1 text-sm text-gray-500">Goes straight to the SchoolOne team, with the page you are on.</p>
                  </div>
                  <button type="button" onClick={close} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close">
                    <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5" aria-hidden="true">
                      <path d="M6 6l8 8M14 6l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                </div>

                <fieldset className="mt-4">
                  <legend className="label">What is it?</legend>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {KINDS.map((k) => (
                      <label key={k.value} className={`cursor-pointer rounded-lg border p-3 text-sm ${kind === k.value ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <input type="radio" name="feedback-kind" value={k.value} checked={kind === k.value} onChange={() => setKind(k.value)} className="sr-only" />
                        <span className="block font-medium text-gray-900">{k.label}</span>
                        <span className="mt-0.5 block text-xs text-gray-500">{k.hint}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <label htmlFor="feedback-message" className="label mt-4">Your message</label>
                <textarea
                  id="feedback-message"
                  ref={textareaRef}
                  className="input min-h-[7rem]"
                  value={message}
                  maxLength={MESSAGE_MAX}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={kind === 'problem' ? 'What were you trying to do, and what happened?' : 'What would make this better for you?'}
                  required
                />
                <div className="mt-1 flex items-center justify-between text-xs text-gray-400">
                  <span>Please do not include pupil names or other personal details.</span>
                  <span>{message.length}/{MESSAGE_MAX}</span>
                </div>

                {error && <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

                <div className="mt-5 flex justify-end gap-2">
                  <button type="button" onClick={close} className="btn-secondary">Cancel</button>
                  <button type="submit" disabled={!message.trim() || state === 'sending'} className="btn-primary">
                    {state === 'sending' ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
